import { Controller } from "@hotwired/stimulus"

// Le panneau latéral d'édition de la fiche du catalogue (produit, fork) : Insurance::Catalog::StepPanelComponent, rendu
// une fois dans la coquille, hors des diapositives. Posé sur la coquille, avec continuous-pages : il voit les crayons de
// toutes les pages (le tableau des paramètres, FieldsComponent) et les « Modifier » de l'en-tête du fork.
//
// - open (action, paramètres de l'élément cliqué : src, frame, title, level, inherits, footer, workflow) : remplit
//   l'en-tête du panneau, monte la frame de l'étape legacy (une seule dans le document : son id ne dépend que du
//   template ; le squelette d'attente y est recopié), ouvre le panneau et y porte le focus, sans le piéger : les
//   modales que les étapes legacy ouvrent (documents, contacts) doivent le prendre.
// - openList (action de la cellule « Répartition sur les templates » : src, title, level, footer) : le mode liste. La
//   frame insurance_field_templates (Insurance::Catalog::FieldTemplatesComponent) : filtres par valeur, templates dans
//   l'ordre de l'arbre, « Afficher plus » ; la recherche (nom, n°, société) est le champ du panneau, hors de la frame
//   (il garde le focus pendant que la liste se recharge, à son adresse ?recherche=). Le crayon d'un template de la
//   liste (open) ouvre son étape dans le même panneau : la liste reste montée, cachée, avec ses filtres, sa recherche,
//   ses pages et son défilement ; « ← Répartition » (back) y revient.
// - Enregistrement réussi : le legacy remplace notre frame par l'étape suivante du parcours, sans invalid=true. On annule
//   ce rendu (`turbo:before-stream-render`, annulable depuis Turbo 8), on recharge l'étape dans le panneau, « Enregistré »,
//   puis le tableau des paramètres de la page visible (frame `[data-insurance-fields-frame]`, à son `data-reload-url`),
//   la liste gardée de côté (mêmes filtres, mêmes pages : ?page=<pages chargées>) et les éléments marqués
//   `data-insurance-step-panel-refresh` (l'en-tête du fork : la page courante est relue, l'élément de même id la
//   remplace). Le flash du legacy passe. Échec : le remplacement porte invalid=true, on le laisse rendre (le formulaire
//   revient avec ses erreurs).
// - L'adresse : aucune navigation dans le panneau ne touche l'adresse ni l'historique de la fiche. Les étapes legacy y
//   montent des turbo index dont les onglets, les tris et les filtres portent data-turbo-action="advance" : Turbo
//   pousserait leur adresse (/fr/dashboard/template_contact_sheet?action=index&…) dans l'historique, et un
//   rechargement sortirait de la fiche. Turbo lit cet attribut sur le bouton d'envoi, le lien ou le formulaire, puis sur
//   la frame visée, au moment où il suit le lien (après le clic) ou reçoit la réponse du formulaire : avant lui (clic et
//   envoi capturés sur le document), on pose sur l'élément une valeur qu'il ne reconnaît pas, qui masque aussi celle de
//   la frame (`keepAddress`), pour toute navigation qui vise une frame du panneau. Une sortie de la page (_top, lien
//   hors frame, nom d'un template de la liste) garde son comportement, comme le legacy hors du panneau.
// - Fermeture : la croix, Échap (sauf quand une modale, un menu, une liste ou une recherche à vider a la main), le
//   changement de page du défilement (`continuous-pages:change`), et avant la mise en cache Turbo (l'instantané garde
//   un panneau fermé).
// - ?etape= : le composant pose l'ouverture demandée en JSON (data-initial du panneau) ; on l'ouvre une fois, puis on
//   l'oublie (un retour arrière ne la rejoue pas).
//
// Sur téléphone, la coquille défile elle-même et contient le panneau : un balayage dans le panneau ne doit pas faire
// glisser la page (action `isolate`, sur touchstart) ; la molette et les touches, continuous-pages les ignore déjà
// (data-continuous-pages-ignore).

const ESCAPE_TAKERS = [
  ".modal", ".dropdown-menu.show", "[role='listbox']", "[role='menu']", ".ts-dropdown", ".select2-container--open",
  ".flatpickr-calendar.open", "[role='combobox'][aria-expanded='true']", "[data-bs-toggle='dropdown'][aria-expanded='true']",
].join(", ")
// Une valeur de data-turbo-action que Turbo ne reconnaît pas (ni advance, ni replace, ni restore) : pas d'historique.
const KEEP_ADDRESS = "none"
// La frame de la liste et celles de ses pages suivantes (Insurance::Catalog::FieldTemplatesComponent).
const LIST_FRAME = "insurance_field_templates"
const SEARCH_PARAM = "recherche"
const PAGE_PARAM = "page"
const SEARCH_DELAY = 250

// Le paramètre d'une étape ouverte dans le panneau (cf. inPanel).
const PANEL_PARAM = "panneau"

export default class extends Controller {
  static targets = [
    "panel", "heading", "back", "eyebrow", "title", "level", "inherits", "search", "searchInput", "status", "scroller",
    "list", "body", "footer", "workflow", "skeleton", "listSkeleton",
  ]

  initialize() {
    this.onBeforeStreamRender = (event) => this.beforeStreamRender(event)
    this.onKeydown = (event) => this.keydown(event)
    this.onBeforeCache = () => this.close({ restoreFocus: false })
    this.onPageChange = () => this.close({ restoreFocus: false })
    this.onNavigate = (event) => this.keepAddress(event)
    this.onFrameRender = (event) => this.listRendered(event)
  }

  connect() {
    document.addEventListener("turbo:before-stream-render", this.onBeforeStreamRender)
    document.addEventListener("keydown", this.onKeydown)
    document.addEventListener("turbo:before-cache", this.onBeforeCache)
    document.addEventListener("click", this.onNavigate, true)
    document.addEventListener("submit", this.onNavigate, true)
    this.element.addEventListener("continuous-pages:change", this.onPageChange)
    this.element.addEventListener("turbo:frame-render", this.onFrameRender)
    this.openRequested()
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.onBeforeStreamRender)
    document.removeEventListener("keydown", this.onKeydown)
    document.removeEventListener("turbo:before-cache", this.onBeforeCache)
    document.removeEventListener("click", this.onNavigate, true)
    document.removeEventListener("submit", this.onNavigate, true)
    this.element.removeEventListener("continuous-pages:change", this.onPageChange)
    this.element.removeEventListener("turbo:frame-render", this.onFrameRender)
    clearTimeout(this.initialTimer)
    clearTimeout(this.searchTimer)
    this.unmount()
    this.discardList()
  }

  get previewing() {
    return document.documentElement.hasAttribute("data-turbo-preview")
  }

  get isOpen() {
    return this.hasPanelTarget && !this.panelTarget.hidden
  }

  // ── Ouverture d'une étape ──────────────────────────────────────────────────────────────────

  // Un lien (« Modifier » de l'en-tête du fork) ouvert avec un modificateur suit son adresse : nouvel onglet. Le crayon
  // d'un template de la liste ouvre son étape en gardant la liste de côté.
  open(event) {
    const params = event.params ?? {}
    if (!params.src || !params.frame) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return

    event.preventDefault()
    const opener = event.currentTarget
    if (this.list && this.hasListTarget && this.listTarget.contains(opener)) {
      this.listScroll = this.scrollerTarget.scrollTop
      this.showStep(params, opener, { fromList: true })
    } else {
      this.show(params, opener)
    }
  }

  // L'étape demandée par l'adresse (?etape=), une seule fois : différée d'un tick, le temps que la page se pose.
  openRequested() {
    if (!this.hasPanelTarget || this.previewing) return

    const raw = this.panelTarget.dataset.initial
    if (!raw) return

    delete this.panelTarget.dataset.initial
    let params
    try {
      params = JSON.parse(raw)
    } catch {
      return
    }
    this.initialTimer = setTimeout(() => {
      if (this.element.isConnected && params.src && params.frame) this.show(params, null)
    }, 0)
  }

  // Une étape ouverte du tableau, de l'en-tête ou de l'adresse : une liste ouverte s'efface.
  show(params, opener) {
    if (!this.hasPanelTarget) return

    this.discardList()
    this.showStep(params, opener, { fromList: false })
  }

  showStep(params, opener, { fromList }) {
    if (this.opener && this.opener !== opener) this.markOpener(this.opener, false)
    this.current = params
    this.opener = opener
    this.fill({ eyebrow: "step", ...params })
    this.backTarget.hidden = !fromList
    this.searchTarget.hidden = true
    this.listTarget.hidden = true
    this.bodyTarget.hidden = false
    this.mount(params)
    this.scrollerTarget.scrollTop = 0
    this.reveal()
    this.markOpener(opener, true)
    this.headingTarget.focus({ preventScroll: true })
  }

  // La frame de l'étape, neuve : l'adresse figée du parcours, le squelette en attendant.
  mount(params) {
    const frame = document.createElement("turbo-frame")
    frame.id = params.frame
    frame.classList.add("insurance-step-panel__frame")
    if (this.hasSkeletonTarget) frame.append(this.skeletonTarget.content.cloneNode(true))
    frame.setAttribute("src", this.inPanel(params.src))
    this.bodyTarget.replaceChildren(frame)
  }

  // L'adresse de l'étape, marquée « ouverte dans le panneau » (?panneau=1) : son bouton dit « Enregistrer » et non
  // « Suivant » (app/views/application/_submit.html.erb), et le formulaire renvoie la marque, gardée après une erreur.
  inPanel(src) {
    const url = new URL(src, window.location.href)
    url.searchParams.set(PANEL_PARAM, "1")
    return url.pathname + url.search
  }

  unmount() {
    if (this.hasBodyTarget) this.bodyTarget.replaceChildren()
  }

  // ── Mode liste : la répartition sur les templates ──────────────────────────────────────────

  openList(event) {
    const params = event.params ?? {}
    if (!params.src || !this.hasPanelTarget) return

    event.preventDefault()
    const opener = event.currentTarget
    this.unmount()
    this.discardList()
    if (this.opener) this.markOpener(this.opener, false)
    this.current = null
    this.opener = null
    this.list = params
    this.listOpener = opener
    this.mountList(params.src)
    this.searchInputTarget.value = ""
    this.showList()
    this.scrollerTarget.scrollTop = 0
    this.reveal()
    this.markOpener(opener, true)
    this.headingTarget.focus({ preventScroll: true })
  }

  showList() {
    this.fill({ eyebrow: "list", ...this.list })
    this.backTarget.hidden = true
    this.bodyTarget.hidden = true
    this.listTarget.hidden = false
    this.searchTarget.hidden = false
  }

  mountList(src) {
    const frame = document.createElement("turbo-frame")
    frame.id = LIST_FRAME
    frame.classList.add("insurance-step-panel__list-frame")
    if (this.hasListSkeletonTarget) frame.append(this.listSkeletonTarget.content.cloneNode(true))
    frame.setAttribute("src", src)
    this.listTarget.replaceChildren(frame)
  }

  discardList() {
    clearTimeout(this.searchTimer)
    if (this.hasListTarget) this.listTarget.replaceChildren()
    if (this.listOpener) this.markOpener(this.listOpener, false)
    this.list = null
    this.listOpener = null
    this.listScroll = 0
  }

  // « ← Répartition » : l'étape d'un template se ferme, la liste revient comme on l'a laissée.
  back() {
    if (!this.list) return

    const opener = this.opener
    this.markOpener(opener, false)
    this.unmount()
    this.current = null
    this.opener = null
    this.statusTarget.hidden = true
    this.showList()
    this.scrollerTarget.scrollTop = this.listScroll ?? 0
    if (opener?.isConnected && !opener.closest("[hidden]")) opener.focus({ preventScroll: true })
    else this.headingTarget.focus({ preventScroll: true })
  }

  get listFrame() {
    return this.hasListTarget ? this.listTarget.querySelector(`turbo-frame#${LIST_FRAME}`) : null
  }

  // L'adresse de ce que la liste montre : common.ts range le src d'une frame chargée dans data-lazy-src.
  get listUrl() {
    const frame = this.listFrame
    const src = frame?.getAttribute("src") || frame?.dataset.lazySrc
    return src ? new URL(src, window.location.href) : null
  }

  search() {
    clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => this.searchNow(), SEARCH_DELAY)
  }

  searchNow(event) {
    event?.preventDefault()
    clearTimeout(this.searchTimer)
    const url = this.listUrl
    if (!url) return

    const query = this.searchInputTarget.value.trim()
    if ((url.searchParams.get(SEARCH_PARAM) ?? "") === query) return

    if (query) url.searchParams.set(SEARCH_PARAM, query)
    else url.searchParams.delete(SEARCH_PARAM)
    url.searchParams.delete(PAGE_PARAM)
    this.reload(this.listFrame, url.pathname + url.search)
  }

  // La liste recharge les pages déjà chargées (« Afficher plus »), pour retrouver son défilement.
  reloadList() {
    const frame = this.listFrame
    const url = this.listUrl
    if (!frame || !url) return

    const pages = this.loadedPages()
    if (pages > 1) url.searchParams.set(PAGE_PARAM, String(pages))
    else url.searchParams.delete(PAGE_PARAM)
    this.reload(frame, url.pathname + url.search)
  }

  loadedPages() {
    const pages = [...this.listTarget.querySelectorAll("[data-templates-page]")].map((list) => Number(list.dataset.templatesPage) || 1)
    return Math.max(1, ...pages)
  }

  // Une page de la liste rendue (filtre, page suivante) : le focus, perdu avec le lien cliqué, va au filtre actif ou au
  // premier template arrivé.
  listRendered(event) {
    const frame = event.target
    if (!(frame instanceof Element) || !this.hasListTarget || this.listTarget.hidden || !this.listTarget.contains(frame)) return

    const active = document.activeElement
    if (active && active !== document.body && active.isConnected) return

    const target = frame.id === LIST_FRAME
      ? frame.querySelector(".insurance-field-templates__filter[aria-current='true']")
      : frame.querySelector(".insurance-field-templates__name")
    target?.focus({ preventScroll: true })
  }

  // ── Affichage ──────────────────────────────────────────────────────────────────────────────

  fill({ eyebrow, title, level, inherits, footer, workflow }) {
    const label = eyebrow === "list" ? this.eyebrowTarget.dataset.listLabel : this.eyebrowTarget.dataset.stepLabel
    this.eyebrowTarget.textContent = label ?? ""
    this.titleTarget.textContent = title ?? ""
    this.levelTarget.textContent = level ?? ""
    this.inheritsTarget.textContent = inherits ?? ""
    this.inheritsTarget.hidden = !inherits
    this.footerTarget.textContent = footer ?? ""
    if (workflow) this.workflowTarget.setAttribute("href", workflow)
    this.workflowTarget.hidden = !workflow
    this.statusTarget.hidden = true
  }

  reveal() {
    this.panelTarget.hidden = false
    this.element.classList.add("is-step-panel-open")
  }

  // ── Fermeture ──────────────────────────────────────────────────────────────────────────────

  close({ restoreFocus = true } = {}) {
    if (!this.isOpen) return

    // La liste ouverte du tableau, sinon ce qui a ouvert l'étape.
    const opener = this.listOpener ?? this.opener
    this.panelTarget.hidden = true
    this.unmount()
    this.markOpener(this.opener, false)
    this.discardList()
    this.element.classList.remove("is-step-panel-open")
    this.current = null
    this.opener = null
    if (restoreFocus && opener?.isConnected && !opener.closest("[hidden]")) opener.focus({ preventScroll: true })
  }

  keydown(event) {
    if (event.key !== "Escape" || !this.isOpen || event.defaultPrevented) return
    if (this.escapeTaken(event.target)) return

    event.preventDefault()
    this.close()
  }

  // Échap appartient d'abord à ce qui est ouvert par-dessus ou dans le panneau : une modale (qui se ferme déjà, d'où
  // body.modal-open), un menu, une liste de choix, un calendrier ; et à la recherche de la liste, qu'il vide.
  escapeTaken(target) {
    if (document.body.classList.contains("modal-open") || document.querySelector(".modal.show")) return true
    if (!(target instanceof Element)) return false
    if (this.hasSearchInputTarget && target === this.searchInputTarget && target.value) return true

    return Boolean(target.closest(ESCAPE_TAKERS))
  }

  markOpener(opener, expanded) {
    if (opener?.hasAttribute?.("aria-haspopup")) opener.setAttribute("aria-expanded", expanded ? "true" : "false")
  }

  // Un balayage commencé dans le panneau ne remonte pas jusqu'au défilement continu de la coquille.
  isolate(event) {
    event.stopPropagation()
  }

  // ── L'adresse de la fiche ──────────────────────────────────────────────────────────────────

  // Clic et envoi, capturés sur le document (avant Turbo) : une navigation qui vise une frame du panneau perd son
  // data-turbo-action (cf. l'en-tête).
  keepAddress(event) {
    if (!this.isOpen) return

    const form = event.type === "submit" ? event.target : null
    const element = form ?? (event.target instanceof Element ? event.target.closest("a[href]") : null)
    if (!(element instanceof Element)) return

    const submitter = form ? event.submitter : null
    const frame = this.navigatedFrame(element, submitter)
    if (!frame || !this.panelTarget.contains(frame)) return

    for (const target of [submitter, element]) {
      if (target instanceof Element) target.setAttribute("data-turbo-action", KEEP_ADDRESS)
    }
  }

  // La frame que Turbo navigue pour ce lien ou ce formulaire (FrameController#findFrameElement) : celle que
  // data-turbo-frame nomme, sinon celle qui le contient ; null pour une sortie de la page (_top, hors de toute frame).
  navigatedFrame(element, submitter) {
    const id = submitter?.getAttribute("data-turbo-frame") || element.getAttribute("data-turbo-frame")
    if (id === "_top") return null

    const named = id ? document.getElementById(id) : null
    if (named?.localName === "turbo-frame") return named

    const frame = element.closest("turbo-frame")
    if (!frame || (!id && frame.getAttribute("target") === "_top")) return null

    return frame
  }

  // ── Enregistrement ─────────────────────────────────────────────────────────────────────────

  get currentFrame() {
    if (!this.current || !this.hasBodyTarget) return null

    return this.bodyTarget.querySelector(`turbo-frame#${CSS.escape(this.current.frame)}`)
  }

  beforeStreamRender(event) {
    if (!this.currentFrame) return

    const stream = event.target
    if (!(stream instanceof Element) || stream.getAttribute("target") !== this.current.frame) return
    if (stream.getAttribute("action") !== "replace") return

    const frame = stream.querySelector(":scope > template")?.content.querySelector("turbo-frame")
    const src = frame?.getAttribute("src")
    if (!src || this.invalid(src)) return

    event.preventDefault()
    this.saved()
  }

  invalid(src) {
    try {
      return new URL(src, window.location.href).searchParams.get("invalid") === "true"
    } catch {
      return false
    }
  }

  // L'étape revient avec ses valeurs enregistrées ; le tableau de la page visible, la liste gardée de côté et l'en-tête
  // du fork aussi.
  saved() {
    this.mount(this.current)
    this.statusTarget.hidden = false
    this.refreshPage()
    if (this.list) this.reloadList()
  }

  refreshPage() {
    const page = this.element.querySelector(".continuous-pages__page:not([hidden])")
    page?.querySelectorAll("turbo-frame[data-insurance-fields-frame][data-reload-url]").forEach((frame) => {
      this.reload(frame, frame.dataset.reloadUrl)
    })
    this.refreshBlocks()
  }

  // Les blocs hors des diapositives (l'en-tête du fork) : relus dans la page courante, remplacés par leur id. Seule la
  // requête est dans le try : une erreur de rendu ne se déguise pas en panne réseau.
  async refreshBlocks() {
    const blocks = [...this.element.querySelectorAll("[data-insurance-step-panel-refresh][id]")]
      .filter((block) => !this.panelTarget.contains(block))
    if (blocks.length === 0) return

    let html
    try {
      const response = await fetch(window.location.href, { headers: { Accept: "text/html" }, credentials: "same-origin" })
      if (!response.ok) return
      html = await response.text()
    } catch {
      return
    }

    const fresh = new DOMParser().parseFromString(html, "text/html")
    for (const block of blocks) {
      const replacement = fresh.getElementById(block.id)
      if (replacement && block.isConnected) block.replaceWith(document.importNode(replacement, true))
    }
  }

  // Comme l'action de stream changeSrc (common.ts) : une frame déjà chargée est réputée gelée, on la dégèle d'abord.
  reload(frame, url) {
    if (!frame || !url) return

    delete frame.dataset.lazyLoaded
    frame.removeAttribute("complete")
    frame.src = url
  }
}
